import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';

import { cn } from '../lib/cn';

/**
 * Is this element wider than its box, and where is it scrolled to? (MASTER_PLAN M11.11.)
 *
 * Measurement, not a media query: whether a table needs to scroll depends on the text
 * inside it, not on the viewport. `ResizeObserver` is guarded because jsdom does not
 * implement it — under test the hook measures once on mount, which is what a unit test
 * can control anyway.
 */
export interface HorizontalOverflow {
  /** `scrollWidth` exceeds `clientWidth` by more than a rounding error. */
  scrollable: boolean;
  /** Scrolled fully to the left (or not scrollable at all). */
  atStart: boolean;
  /** Scrolled fully to the right (or not scrollable at all). */
  atEnd: boolean;
}

const NOT_SCROLLABLE: HorizontalOverflow = { scrollable: false, atStart: true, atEnd: true };

/** Tolerance in px: sub-pixel layout makes an exact comparison flap. */
const EPSILON = 1;

export function measureHorizontalOverflow(element: HTMLElement | null): HorizontalOverflow {
  if (element === null) return NOT_SCROLLABLE;
  const { scrollWidth, clientWidth, scrollLeft } = element;
  if (scrollWidth - clientWidth <= EPSILON) return NOT_SCROLLABLE;
  return {
    scrollable: true,
    atStart: scrollLeft <= EPSILON,
    atEnd: scrollWidth - clientWidth - scrollLeft <= EPSILON,
  };
}

export function useHorizontalOverflow(ref: RefObject<HTMLElement | null>): HorizontalOverflow {
  const [state, setState] = useState<HorizontalOverflow>(NOT_SCROLLABLE);

  const measure = useCallback(() => {
    const next = measureHorizontalOverflow(ref.current);
    setState((previous) =>
      previous.scrollable === next.scrollable &&
      previous.atStart === next.atStart &&
      previous.atEnd === next.atEnd
        ? previous
        : next,
    );
  }, [ref]);

  useEffect(() => {
    measure();
    const element = ref.current;
    if (element === null) return undefined;

    element.addEventListener('scroll', measure, { passive: true });

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => {
        element.removeEventListener('scroll', measure);
        window.removeEventListener('resize', measure);
      };
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    /* The content, not just the box: a table that grows a column has to re-measure. */
    if (element.firstElementChild !== null) observer.observe(element.firstElementChild);
    return () => {
      element.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [measure, ref]);

  return state;
}

export interface ScrollAreaProps {
  /**
   * Names the region for screen readers while it is scrollable. Reuse the table's
   * caption: an unnamed landmark is worse than none.
   */
  label: string;
  /** Classes for the scrolling element itself. */
  className?: string;
  /** Classes for the positioning wrapper that carries the edge fades. */
  wrapperClassName?: string;
  children: ReactNode;
}

/**
 * A horizontally scrolling region that a keyboard can reach (MASTER_PLAN M11.11,
 * rules §48.2, §48.6, §49.4).
 *
 * Three things a bare `overflow-x-auto` div gets wrong, all of them found by the M11.11
 * audit and all of them fixed here:
 *
 *  1. **It is not reachable.** A mouse can drag it and a trackpad can swipe it, but a
 *     keyboard cannot, so the columns past the fold are unreachable without a pointer.
 *     `tabIndex={0}` with `role="region"` and a name fixes it — and only while the
 *     content actually overflows, so a table that fits is not a pointless tab stop.
 *     (This is axe's `scrollable-region-focusable`.)
 *  2. **It leaks a page-wide horizontal scrollbar.** `sr-only` text is
 *     `position: absolute`, so inside a scroller with no positioned ancestor its 1px box
 *     is laid out against the *initial* containing block, escapes the scroller's clip
 *     and stretches `document.documentElement.scrollWidth` to the full width of the
 *     table. `relative` on the scroller makes it the containing block and the phantom
 *     scrollbar disappears. This alone was five of the audit's overflow findings.
 *  3. **It hides its own affordance.** Clipped content looks like content that ends —
 *     KI-01, where the dashboard's readiness chips were cut to "IN" and "C". A fade at
 *     whichever edge still has content says "there is more this way".
 *
 * The focus ring is drawn *inside* the box (`outline-offset: -2px`) because these
 * regions sit in cards with `overflow: hidden`, which would clip an outset ring.
 */
export function ScrollArea({ label, className, wrapperClassName, children }: ScrollAreaProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollable, atStart, atEnd } = useHorizontalOverflow(ref);

  return (
    <div className={cn('relative', wrapperClassName)}>
      <div
        ref={ref}
        data-scrollable={scrollable ? 'true' : 'false'}
        className={cn('relative overflow-x-auto focus-visible:outline-offset-[-2px]', className)}
        {...(scrollable ? { tabIndex: 0, role: 'region', 'aria-label': label } : {})}
      >
        {children}
      </div>
      {scrollable && !atStart ? (
        <span
          aria-hidden="true"
          data-testid="scroll-fade-start"
          className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-ink/10 to-transparent"
        />
      ) : null}
      {scrollable && !atEnd ? (
        <span
          aria-hidden="true"
          data-testid="scroll-fade-end"
          className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-ink/10 to-transparent"
        />
      ) : null}
    </div>
  );
}
